import React, { useState, useEffect, useCallback } from "react";
import {
  TableColumn,
  StandardizedTable,
  formatNumber as stdFormatNumber,
  formatCurrency as stdFormatCurrency,
  TrendIndicator,
  TableRow,
} from "@/components/ui/standardized-table";
import type { TableQueryDTO } from "@/types/table-query";
import { getTopCategorySalesApiV1TopCategorySalesGet } from "@/backend-sdk/client/sdk.gen";
import type {
  GetTopCategorySalesApiV1TopCategorySalesGetData,
  TopCategorySalesAsyncResponse,
} from "@/backend-sdk/client/types.gen";
import { useAccount } from "@/contexts/account.context";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { FilterState, formatDateToISO } from "@/features/filters/filter.types";
import { ValorIndisponivel } from "@/components/ui/estado-do-valor";
import type { RequisitoDeFonte } from "@/lib/motivo-do-indisponivel";
import { useDrilldownOptional } from "@/features/drilldown";

/** As categorias saem do e-commerce do GA4. */
const FONTES_DA_TABELA: readonly RequisitoDeFonte[] = ["ga4"];

interface CategoriasMaisVendidasTableProps {
  itemName?: string;
  itemNameOperator?: string;
  compact?: boolean;
  excludeColumns?: string[];
  /**
   * <M-212> Se a página em que a tabela está desenhada tem como honrar o recorte que o
   * clique produz.
   *
   * A tabela se liga sozinha ao `DrilldownProvider` — ela não recebe `onRowClick` da
   * página —, e por isso era a página que não tinha como dizer "aqui o clique não vale".
   *
   * ⚠️ **O padrão é `false`, e é de propósito.** Das sete telas que montam drill, só a
   * Produtos honra o recorte de item, então a omissão tem de cair no lado seguro: uma tela
   * nova que desenhe esta tabela sem dizer nada não passa a afirmar um recorte que não
   * chega. Ligar é uma decisão, e quem a toma confere antes que as rotas **daquela** página
   * aplicam `filter_category`. Ver **D-054** e **D-056**.
   */
  drilldownEnabled?: boolean;
}

type TopCategorySalesCrossFilterKey = Extract<
  keyof GetTopCategorySalesApiV1TopCategorySalesGetData["query"],
  `filter_${string}`
>;
type TopCategorySalesCrossFilters = Pick<
  GetTopCategorySalesApiV1TopCategorySalesGetData["query"],
  TopCategorySalesCrossFilterKey
>;

const getTopCategorySalesCrossFilters = (
  filters: Record<string, string> | undefined,
): Partial<TopCategorySalesCrossFilters> => ({
  filter_search_term: filters?.filter_search_term,
  filter_device: filters?.filter_device,
  filter_region: filters?.filter_region,
  filter_city: filters?.filter_city,
  filter_category: filters?.filter_category,
  // <M-212> `filter_category2` a `filter_category5`, `filter_brand` e `filter_variant` saíram:
  // `DrillDimension` não tem mais as seis dimensões, então `apiFilters` não tem como trazer
  // as chaves. As rotas continuam declarando os parâmetros — o que sumiu é quem os produzia.
  filter_product: filters?.filter_product,
  filter_date: filters?.filter_date,
});

const CategoriasMaisVendidasTable: React.FC<
  CategoriasMaisVendidasTableProps
> = ({
  itemName,
  itemNameOperator = "contains",
  compact = false,
  excludeColumns,
  drilldownEnabled = false,
}) => {
  const { selectedAccount } = useAccount();
  const { filter: globalFilter } = useGlobalFilter();
  const contextoDeDrill = useDrilldownOptional();
  // <M-212> O contexto continua sendo lido para os filtros que **outras** tabelas produzem
  // — `filter_device` e `filter_date`, os dois que `/top-category-sales` aplica —; o que
  // `drilldownEnabled` desliga é a emissão, isto é, o clique nesta tabela.
  const drilldown = drilldownEnabled ? contextoDeDrill : null;
  const [topCategoryData, setTopCategoryData] =
    useState<TopCategorySalesAsyncResponse | null>(null);
  // Começa carregando: a busca só sai no efeito, e a tabela vazia antes dela parecia "sem dados".
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const PAGE_SIZE = 10;
  const [tableQuery, setTableQuery] = useState<TableQueryDTO>({});

  // Handler para clique na linha (cross-filtering com suporte a Ctrl+click multi-select)
  const handleRowClick = useCallback(
    (row: TableRow, index: number, event?: React.MouseEvent) => {
      if (!drilldown) return;

      const categoryName = row.category_name as string;
      if (!categoryName || categoryName === "N/D") return;

      const isMultiSelect = event?.ctrlKey || event?.metaKey;
      drilldown.selectRow("category", categoryName, categoryName, isMultiSelect);
    },
    [drilldown],
  );

  // Verifica se uma linha está selecionada
  const isRowSelected = useCallback(
    (row: TableRow, index: number): boolean => {
      if (!drilldown) return false;
      const categoryName = row.category_name as string;
      return drilldown.isSelected("category", categoryName);
    },
    [drilldown],
  );

  const renderND = () => (
    <ValorIndisponivel fontes={FONTES_DA_TABELA} erro={error !== null} />
  );

  const renderDeltaCell = (value: unknown): React.ReactNode => {
    const change = typeof value === "number" ? value : null;
    if (change === null || Number.isNaN(change)) {
      return <span className="text-muted-foreground">—</span>;
    }
    return <TrendIndicator value={change} />;
  };

  // Colunas completas
  const allColumns: TableColumn[] = [
    {
      key: "category_name",
      header: "Categoria do Item",
      align: "left",
      render: (value, item, index, isTotal) => {
        if (isTotal) return value;
        const isND = !value || String(value).toUpperCase() === "N/D";
        const displayIndex = Number(item._index ?? index + 1);
        return `${displayIndex}. ${isND ? "N/D" : value}`;
      },
    },
    {
      key: "items_viewed",
      header: "Itens Vistos",
      align: "right",
      type: "number",
      render: (value) =>
        !value || value === 0 ? renderND() : stdFormatNumber(value),
    },
    {
      key: "items_viewed_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "sessions",
      header: "Sessões",
      align: "right",
      type: "number",
      visible: false,
      render: (value) => {
        const numeric = typeof value === "number" ? value : Number(value || 0);
        const hasValue = Number.isFinite(numeric) && numeric > 0;
        return !hasValue ? renderND() : stdFormatNumber(numeric);
      },
    },
    {
      key: "sessions_change_percentage",
      header: "% Δ",
      align: "right" as const,
      visible: false,
      render: renderDeltaCell,
    },
    {
      key: "session_conversion_rate",
      header: "Tx Conv. da Sessão",
      align: "right",
      type: "percent",
      visible: false,
      render: (value) => {
        const numeric = typeof value === "number" ? value : Number(value || 0);
        const hasValue = Number.isFinite(numeric) && numeric > 0;
        return !hasValue ? renderND() : `${numeric.toFixed(2)}%`;
      },
    },
    {
      key: "session_conversion_rate_change",
      header: "% Δ",
      align: "right" as const,
      visible: false,
      // `TopCategorySalesItem` não publica esta variação — ela só existe dentro de `changes`,
      // e em `queryMode="server"` o `sort_by` emitido é a chave da coluna.
      sortable: false,
      render: renderDeltaCell,
    },
    {
      key: "items_added_to_cart",
      header: "Carrinhos",
      align: "right",
      type: "number",
      render: (value) =>
        !value || value === 0 ? renderND() : stdFormatNumber(value),
    },
    {
      key: "items_added_to_cart_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "cart_rate",
      header: "Tx Carrinho",
      align: "right",
      type: "percent",
      // <M-200> A taxa vem pronta do servidor, como no card irmão. Ela era calculada aqui, e
      // por isso as duas colunas não podiam ser ordenáveis: em `queryMode="server"` o
      // `sort_by` emitido é a chave da coluna, e o campo não existia na resposta.
      //
      // ⚠️ O campo é opcional no SDK, e uma resposta sem ele desenha "N/D", não "0,00 %":
      // esta tela pode subir antes da API, ou receber uma resposta em cache de antes dela. Um
      // zero ao lado de colunas com visualizações e carrinhos seria um número errado sem
      // aviso.
      render: (value, item) => {
        const vistos = item?.items_viewed || 0;
        if (vistos === 0 || typeof value !== "number") return renderND();
        return `${value.toFixed(2)}%`;
      },
    },
    {
      key: "cart_rate_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "items_purchased",
      header: "Itens Comprados",
      align: "right",
      type: "number",
      render: (value) =>
        !value || value === 0 ? renderND() : stdFormatNumber(value),
    },
    {
      key: "items_purchased_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "conversion_rate",
      header: "Tx Conv. Itens",
      align: "right",
      type: "percent",
      // <M-202> A taxa vem pronta do servidor, e a célula a recalculava por cima —
      // `items_purchased / items_added_to_cart`, a mesma fórmula, sobre as duas colunas que
      // a resposta traz ao lado dela. Era o certo enquanto o rodapé publicava
      // `conversion_rate: 0` literal; desde M-200 as duas taxas do rodapé são a taxa das
      // somas, e recalcular deixou de ter motivo.
      //
      // ⚠️ A última casa decimal muda em empates exatos, como na coluna irmã: o `toFixed(2)`
      // arredondava meio para cima sobre a divisão feita aqui, e o servidor arredonda meio
      // para o par. A coluna passa a mostrar o mesmo número que a ordenação usa.
      //
      // O "N/D" continua vindo do denominador: sem carrinho não há conversão a mostrar, e
      // ali o servidor publica `0`.
      render: (value, item) => {
        const carrinhos = item?.items_added_to_cart || 0;
        if (carrinhos === 0 || typeof value !== "number") return renderND();
        return `${value.toFixed(2)}%`;
      },
    },
    {
      key: "conversion_rate_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
    {
      key: "purchase_revenue",
      header: "Receita do Item",
      align: "right",
      type: "currency",
      render: (value) =>
        !value || value === 0 ? renderND() : stdFormatCurrency(value),
    },
    {
      key: "purchase_revenue_change_percentage",
      header: "% Δ",
      align: "right" as const,
      render: renderDeltaCell,
    },
  ];

  // Modo compacto: apenas Nome, Itens Comprados e Receita do Item (com deltas)
  const compactKeys = new Set([
    "category_name",
    "items_purchased",
    "items_purchased_change_percentage",
    "purchase_revenue",
    "purchase_revenue_change_percentage",
  ]);

  const columns: TableColumn[] = (() => {
    let cols = compact
      ? allColumns.filter((c) => compactKeys.has(c.key))
      : allColumns;
    if (excludeColumns?.length) {
      // A coluna "% Δ" de uma métrica pode chamar-se `<k>_change` (quando a variação só vive
      // em `changes`) ou `<k>_change_percentage` (quando o servidor a publica achatada).
      const excludeSet = new Set(
        excludeColumns.flatMap((k) => [
          k,
          `${k}_change`,
          `${k}_change_percentage`,
        ]),
      );
      cols = cols.filter((c) => !excludeSet.has(c.key));
    }
    return cols;
  })();

  const fetchTopCategorySalesData = useCallback(
    async (filter: FilterState, pg: number = 1) => {
      try {
        setLoading(true);
        setError(null);

        const startDate = formatDateToISO(filter.dateRange.startDate);
        const endDate = formatDateToISO(filter.dateRange.endDate);

        const customerUuid =
          selectedAccount?.customer_uuid ||
          selectedAccount?.id ||
          localStorage.getItem("active_customer_uuid") ||
          undefined;

        if (!customerUuid) return;

        const crossFilters = getTopCategorySalesCrossFilters(
          contextoDeDrill?.apiFilters,
        );
        // A tabela não se filtra por si mesma: a lista de categorias tem de continuar
        // inteira para que a linha selecionada tenha de onde ser desmarcada.
        delete crossFilters.filter_category;

        const { data } = await getTopCategorySalesApiV1TopCategorySalesGet({
          query: {
            dimension: "category",
            ...crossFilters,
            start_date: startDate,
            end_date: endDate,
            period: filter.period,
            comparison: filter.comparison?.type || "previous_period",
            comparison_label: filter.comparison?.label,
            custom_start_date:
              filter.period === "custom"
                ? filter.customDateRange?.startDate?.toISOString().split("T")[0]
                : undefined,
            custom_end_date:
              filter.period === "custom"
                ? filter.customDateRange?.endDate?.toISOString().split("T")[0]
                : undefined,
            customer_uuid: customerUuid,
            page: pg,
            page_size: PAGE_SIZE,
            q: tableQuery?.q,
            sort_by: tableQuery?.sortBy,
            sort_dir: tableQuery?.sortDir,
            filters: tableQuery?.filters?.length
              ? JSON.stringify(tableQuery.filters)
              : undefined,
          },
          throwOnError: true,
        });

        setTopCategoryData(data);
        setPage(data.pagination?.page || pg);
        setTotal(data.pagination?.total ?? data.total_items ?? 0);
      } catch (err) {
        console.error(
          "❌ Erro ao buscar dados da categoria com mais vendas:",
          err,
        );
        setError(err instanceof Error ? err.message : "Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    },
    [selectedAccount, tableQuery, contextoDeDrill],
  );

  // Resetar para a primeira página quando filtros globais mudarem
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1);
  }, [
    globalFilter?.dateRange?.startDate,
    globalFilter?.dateRange?.endDate,
    globalFilter?.comparison?.type,
    globalFilter?.comparison?.label,
    globalFilter?.period,
    contextoDeDrill?.apiFiltersKey,
  ]);

  // Sempre que filtros globais / paginação / query da tabela mudarem, refetch no server
  useEffect(() => {
    if (!globalFilter) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchTopCategorySalesData(globalFilter, page);
  }, [
    globalFilter,
    page,
    fetchTopCategorySalesData,
    contextoDeDrill?.apiFiltersKey,
  ]);

  // Preparar dados para a tabela.
  //
  // As colunas usam a chave que o servidor publica, sem renomear: em `queryMode="server"` o
  // `sort_by` que a tabela emite é a chave da coluna, e um nome que só existe aqui pediria à
  // rota uma ordenação por um campo que a resposta dela não tem. <M-200> Sobrou **uma**
  // exceção, `session_conversion_rate`, cuja variação só vive em `changes`; a coluna dela é
  // declarada não ordenável. A de `cart_rate` deixou de ser exceção quando a rota passou a
  // publicar a família inteira.
  const data = topCategoryData?.items?.length
    ? topCategoryData.items.map((item, index) => {
        const ch = item.changes || {};
        return {
          ...item,
          items_viewed: item.items_viewed || 0,
          sessions:
            typeof item.sessions === "number"
              ? item.sessions
              : Number(item.sessions || 0),
          session_conversion_rate:
            typeof item.session_conversion_rate === "number"
              ? item.session_conversion_rate
              : Number(item.session_conversion_rate || 0),
          session_conversion_rate_change: ch.session_conversion_rate ?? null,
          _index: index + 1,
        };
      })
    : [];

  // Totais (preferir total_row vindo do backend, para respeitar paginação/filtros server-side)
  const totalRow = topCategoryData?.total_row
    ? {
        ...topCategoryData.total_row,
        changes: topCategoryData.total_row.changes || {},
        // O rodapé não desenha as variações: `linhaDeTotalDeCategorias` publica as cinco como
        // zeros literais — não são a variação do total —, e desenhá-las seria um "0%" falso
        // ao lado de cada soma.
        items_viewed_change_percentage: null,
        items_added_to_cart_change_percentage: null,
        // <M-200> O servidor já a manda nula — não há período anterior de onde tirar a
        // variação de uma taxa somada. Explicitada aqui junto das outras, para que a lista
        // continue sendo o inventário do que o rodapé não desenha.
        cart_rate_change_percentage: null,
        items_purchased_change_percentage: null,
        conversion_rate_change_percentage: null,
        purchase_revenue_change_percentage: null,
        sessions_change_percentage: null,
      }
    : null;

  return (
    <StandardizedTable
      title="CATEGORIAS COM MAIS VENDAS"
      description={
        drilldown
          ? "Clique em uma linha para filtrar a página inteira"
          : "Categorias mais vendidas no período"
      }
      columns={columns}
      // Numa nova busca, as linhas anteriores ficam sob o véu de carregamento da tabela.
      data={error ? [] : data}
      loading={loading}
      total={totalRow ?? undefined}
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total: total,
        onPageChange: (p) => {
          setPage(p);
        },
      }}
      queryMode="server"
      onQueryChange={(q) => {
        setTableQuery(q);
        setPage(1);
      }}
      onRowClick={drilldown ? handleRowClick : undefined}
      selectedRowIndex={drilldown ? isRowSelected : undefined}
      clickableRows={!!drilldown}
    />
  );
};

export default CategoriasMaisVendidasTable;
